import { type PortalMember } from "@/hooks/useUsers";
import { formatRelativeTime } from "@/lib/relativeTime";
import { Button } from "@simplicity/ui";
import { Trash2 } from "lucide-react";

export function UserRow({
  user,
  isCurrentUser,
  onEdit,
  onRemove,
}: {
  user: PortalMember;
  isCurrentUser: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- intentional: empty-string name falls through to email
  const displayName = user.name || user.email;
  return (
    // eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- row click surface with nested remove action; semantic <button> would nest interactives.
    <div
      role="button"
      tabIndex={0}
      className="flex items-center justify-between px-4 py-3 text-sm cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={onEdit}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit();
        }
      }}
    >
      <div className="flex items-center gap-3">
        <span className="font-medium">{displayName}</span>
        <span className="text-muted-foreground text-xs">{user.email}</span>
        {user.role === "admin" && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
            Admin
          </span>
        )}
      </div>
      <div className="flex items-center gap-4">
        <span
          className="text-xs text-muted-foreground whitespace-nowrap"
          title={user.lastSeenAt ? new Date(user.lastSeenAt).toLocaleString() : "Never"}
        >
          {formatRelativeTime(user.lastSeenAt)}
        </span>
        {!isCurrentUser && (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto w-auto p-1 text-muted-foreground hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            aria-label="Remove user"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
